import { computed, ref, watch } from 'vue'
import { api } from '@/global/services/api'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  STATUS_COLORS,
  STATUS_PERIOD,
  STATUS_TINTS,
  WORK_ORDERS_STATUS_ENDPOINT,
  WORK_ORDERS_STATUS_TRANSLATION_NAMESPACE as NS
} from '@/modules/road-maintenance-module/winter-service-v2/work-orders/status/config/constants'

const EMPTY_DASHBOARD = {
  vehicles: {},
  work_orders: {},
  intervention_orders: {},
  cleared_kilometers: {},
  warnings: {}
}

export function useStatusDashboard (options) {
  const { translate } = options || {}

  const period = ref(STATUS_PERIOD.today)
  const dashboard = ref({ ...EMPTY_DASHBOARD })
  const isLoading = ref(false)

  const periodItems = computed(() => ([
    {
      id: STATUS_PERIOD.today,
      name: translate(`${NS}.period_today`)
    },
    {
      id: STATUS_PERIOD.last7Days,
      name: translate(`${NS}.period_last_7_days`)
    },
    {
      id: STATUS_PERIOD.last30Days,
      name: translate(`${NS}.period_last_30_days`)
    }
  ]))

  const periodFieldConfig = computed(() => ({
    key: 'period',
    items: periodItems.value,
    clearable: false
  }))

  const fetchDashboard = async () => {
    try {
      isLoading.value = true
      const { data } = await api(WINTER_SERVICE_MODULE).get(WORK_ORDERS_STATUS_ENDPOINT, {
        period: period.value
      })

      dashboard.value = {
        ...EMPTY_DASHBOARD,
        ...(data || {})
      }
    }
    catch (exception) {
      console.error(exception)
      dashboard.value = { ...EMPTY_DASHBOARD }
    }
    finally {
      isLoading.value = false
    }
  }

  const toCount = (value) => Number(value) || 0

  const vehiclesCardConfig = computed(() => {
    const vehicles = dashboard.value.vehicles || {}

    return {
      title: translate(`${NS}.vehicles`),
      totalLabel: translate(`${NS}.total_vehicles`),
      total: toCount(vehicles.total),
      meters: [
        {
          key: 'on_assignment_turned_on',
          label: translate(`${NS}.on_assignment_turned_on`),
          value: toCount(vehicles.on_assignment_turned_on),
          color: STATUS_COLORS.good
        },
        {
          key: 'on_assignment_turned_off',
          label: translate(`${NS}.on_assignment_turned_off`),
          value: toCount(vehicles.on_assignment_turned_off),
          color: STATUS_COLORS.warning
        },
        {
          key: 'off_assignment',
          label: translate(`${NS}.off_assignment`),
          value: toCount(vehicles.off_assignment),
          color: STATUS_COLORS.serious
        },
        {
          key: 'defective',
          label: translate(`${NS}.defective`),
          value: toCount(vehicles.defective),
          color: STATUS_COLORS.critical
        }
      ]
    }
  })

  // Work orders and intervention orders share the same three states, so they share
  // one builder - the two cards can never disagree on wording or colour.
  const buildOrdersCard = (title, orders) => ({
    title,
    totalLabel: translate(`${NS}.total_orders`),
    total: toCount(orders?.total),
    meters: [
      {
        key: 'executed',
        label: translate(`${NS}.executed`),
        value: toCount(orders?.executed),
        color: STATUS_COLORS.good
      },
      {
        key: 'active',
        label: translate(`${NS}.active`),
        value: toCount(orders?.active),
        color: STATUS_COLORS.warning
      },
      {
        key: 'inactive',
        label: translate(`${NS}.inactive`),
        value: toCount(orders?.inactive),
        color: STATUS_COLORS.critical
      }
    ]
  })

  const workOrdersCardConfig = computed(() => buildOrdersCard(
    translate(`${NS}.work_orders`),
    dashboard.value.work_orders
  ))

  const interventionOrdersCardConfig = computed(() => buildOrdersCard(
    translate(`${NS}.intervention_orders`),
    dashboard.value.intervention_orders
  ))

  const clearedKilometersCardConfig = computed(() => {
    const cleared = dashboard.value.cleared_kilometers || {}

    return {
      title: translate(`${NS}.cleared_kilometers`),
      cleared: toCount(cleared.cleared),
      planned: toCount(cleared.planned)
    }
  })

  const warningsCardConfig = computed(() => {
    const warnings = dashboard.value.warnings || {}

    return {
      title: translate(`${NS}.warnings`),
      tiles: [
        {
          key: 'defective_vehicles',
          label: translate(`${NS}.defective_vehicles`),
          value: toCount(warnings.defective_vehicles),
          color: STATUS_COLORS.critical,
          tint: STATUS_TINTS.critical
        },
        {
          key: 'marked_problems',
          label: translate(`${NS}.marked_problems`),
          value: toCount(warnings.marked_problems),
          color: STATUS_COLORS.warning,
          tint: STATUS_TINTS.warning
        },
        {
          key: 'intervention_orders',
          label: translate(`${NS}.intervention_orders_count`),
          value: toCount(warnings.intervention_orders),
          color: STATUS_COLORS.neutral,
          tint: STATUS_TINTS.neutral
        }
      ]
    }
  })

  watch(period, fetchDashboard, { immediate: true })

  return {
    period,
    periodFieldConfig,
    isLoading,
    vehiclesCardConfig,
    workOrdersCardConfig,
    interventionOrdersCardConfig,
    clearedKilometersCardConfig,
    warningsCardConfig,
    fetchDashboard
  }
}
